import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm/reports/preventive-schedule';

const PreventiveSchedule = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const navigate = useNavigate();
  const T = (ar, en) => isRTL ? ar : en;

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [viewMode, setViewMode] = useState('list');

  const today = new Date();
  const [filters, setFilters] = useState({
    preset: 'month',
    start_date: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0],
    client_id: '', priority: '',
  });

  const presets = [
    { key: 'today', label: T('اليوم', 'Today') },
    { key: 'week', label: T('هذا الأسبوع', 'This Week') },
    { key: 'month', label: T('هذا الشهر', 'This Month') },
    { key: 'quarter', label: T('3 أشهر', '3 Months') },
    { key: 'custom', label: T('مخصص', 'Custom') },
  ];

  const applyPreset = (key) => {
    const now = new Date();
    let s, e;
    if (key === 'today') { s = e = now.toISOString().split('T')[0]; }
    else if (key === 'week') {
      const day = now.getDay();
      const ws = new Date(now); ws.setDate(now.getDate() - day);
      const we = new Date(ws); we.setDate(ws.getDate() + 6);
      s = ws.toISOString().split('T')[0]; e = we.toISOString().split('T')[0];
    } else if (key === 'month') {
      s = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      e = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    } else if (key === 'quarter') {
      s = now.toISOString().split('T')[0];
      e = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate()).toISOString().split('T')[0];
    }
    if (key !== 'custom') setFilters(prev => ({ ...prev, preset: key, start_date: s, end_date: e }));
    else setFilters(prev => ({ ...prev, preset: key }));
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/v1/fsm/clients?limit=500', { headers: { Authorization: `Bearer ${token}` } });
        const d = await res.json();
        if (d.success) setClients(d.data || []);
      } catch (e) { console.error(e); }
    };
    fetchClients();
  }, []);

  useEffect(() => { fetchSchedule(); }, [filters.start_date, filters.end_date, filters.client_id, filters.priority]);

  const fetchSchedule = async () => {
    if (!filters.start_date || !filters.end_date) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ start_date: filters.start_date, end_date: filters.end_date });
      if (filters.client_id) params.append('client_id', filters.client_id);
      if (filters.priority) params.append('priority', filters.priority);
      const res = await fetch(`${API}?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) { setData(d.data || []); setSummary(d.summary || {}); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleRow = (idx) => setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));

  const cardSt = (bc) => ({ background: 'rgba(26,32,56,0.6)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 20, borderTop: `3px solid ${bc}`, minWidth: 140 });
  const inputSt = { width: '100%', padding: '10px 14px', border: '2px solid rgba(255,255,255,0.15)', borderRadius: 8, backgroundColor: '#1a2038', color: '#f9fafb', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
  const pColors = { critical: '#f87171', high: '#fb923c', medium: '#fbbf24', low: '#60a5fa' };
  const pLabels = { critical: T('حرجة', 'Critical'), high: T('عالية', 'High'), medium: T('متوسطة', 'Medium'), low: T('منخفضة', 'Low') };
  const recLabels = { daily: T('يومي', 'Daily'), weekly: T('أسبوعي', 'Weekly'), biweekly: T('نصف شهري', 'Bi-weekly'), monthly: T('شهري', 'Monthly'), bimonthly: T('كل شهرين', 'Bi-monthly'), quarterly: T('ربع سنوي', 'Quarterly'), semi_annual: T('نصف سنوي', 'Semi-Annual'), annual: T('سنوي', 'Annual') };

  const fmtDate = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); };

  const createWO = (item) => {
    const params = new URLSearchParams({
      wo_type: 'preventive',
      source: 'maintenance_plan',
      priority: item.priority || 'medium',
      scheduled_date: item.due_date,
      title: `PM: ${item.plan_name}`,
      title_ar: item.plan_name_ar ? ('صيانة وقائية: ' + item.plan_name_ar) : '',
      description: [item.description, item.contract_number ? ('Contract: ' + item.contract_number) : ''].filter(Boolean).join(' | '),
    });
    if (item.client_id) params.set('client_id', String(item.client_id));
    if (item.contract_id) params.set('contract_id', String(item.contract_id));
    if (item.equipment.length === 1) params.set('equipment_id', String(item.equipment[0].equipment_id));
    navigate('/fsm/work-orders/new?' + params.toString(), {
      state: {
        checklist: (item.checklist || []).map(ci => ({
          item: ci.text || ci.item || (typeof ci === 'string' ? ci : ''),
          item_ar: '',
          required: false
        })),
        instructions: item.instructions || '',
        instructions_ar: item.instructions_ar || '',
      }
    });
  };

  const groupedByClient = data.reduce((acc, item) => {
    const key = item.client_id || 'unknown';
    if (!acc[key]) acc[key] = { client_name: item.client_name || T('غير محدد', 'Unknown'), client_name_ar: item.client_name_ar, items: [] };
    acc[key].items.push(item);
    return acc;
  }, {});

  const groupedByDate = data.reduce((acc, item) => {
    if (!acc[item.due_date]) acc[item.due_date] = [];
    acc[item.due_date].push(item);
    return acc;
  }, {});

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: 24, color: '#f9fafb' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>📅 {T('جدول الصيانة الوقائية', 'Preventive Maintenance Schedule')}</h1>
          <p style={{ margin: '6px 0 0', color: '#9ca3af', fontSize: 14 }}>{T('أعمال الصيانة المطلوبة من واقع خطط الصيانة المرتبطة بالعقود السارية', 'Scheduled maintenance from plans linked to active contracts')}</p>
        </div>

        {/* Filters */}
        <div style={{ background: 'rgba(26,32,56,0.8)', padding: 20, borderRadius: 12, marginBottom: 20, border: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {presets.map(p => (
              <button key={p.key} onClick={() => applyPreset(p.key)} style={{
                padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: filters.preset === p.key ? '#3b82f6' : 'rgba(255,255,255,0.08)',
                color: filters.preset === p.key ? '#fff' : '#9ca3af',
              }}>{p.label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 160 }}>
              <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{T('من', 'From')}</label>
              <input type="date" value={filters.start_date} onChange={(e) => setFilters(prev => ({ ...prev, preset: 'custom', start_date: e.target.value }))} style={inputSt} />
            </div>
            <div style={{ minWidth: 160 }}>
              <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{T('إلى', 'To')}</label>
              <input type="date" value={filters.end_date} onChange={(e) => setFilters(prev => ({ ...prev, preset: 'custom', end_date: e.target.value }))} style={inputSt} />
            </div>
            <div style={{ minWidth: 180 }}>
              <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{T('العميل', 'Client')}</label>
              <select value={filters.client_id} onChange={(e) => setFilters(prev => ({ ...prev, client_id: e.target.value }))} style={inputSt}>
                <option value="">{T('جميع العملاء', 'All Clients')}</option>
                {clients.map(c => <option key={c.id} value={c.id}>{isRTL ? (c.name_ar || c.name) : c.name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 140 }}>
              <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{T('الأولوية', 'Priority')}</label>
              <select value={filters.priority} onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))} style={inputSt}>
                <option value="">{T('الكل', 'All')}</option>
                <option value="critical">{T('حرجة', 'Critical')}</option>
                <option value="high">{T('عالية', 'High')}</option>
                <option value="medium">{T('متوسطة', 'Medium')}</option>
                <option value="low">{T('منخفضة', 'Low')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={cardSt('#3b82f6')}><div style={{ fontSize: 12, color: '#9ca3af' }}>{T('إجمالي الأعمال', 'Total Activities')}</div><div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{summary.total_activities || 0}</div></div>
          <div style={cardSt('#f87171')}><div style={{ fontSize: 12, color: '#9ca3af' }}>{T('متأخرة', 'Overdue')}</div><div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: '#f87171' }}>{summary.overdue || 0}</div></div>
          <div style={cardSt('#4ade80')}><div style={{ fontSize: 12, color: '#9ca3af' }}>{T('العملاء', 'Clients')}</div><div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: '#4ade80' }}>{summary.total_clients || 0}</div></div>
          <div style={cardSt('#c084fc')}><div style={{ fontSize: 12, color: '#9ca3af' }}>{T('الخطط', 'Plans')}</div><div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: '#c084fc' }}>{summary.total_plans || 0}</div></div>
          <div style={cardSt('#fbbf24')}><div style={{ fontSize: 12, color: '#9ca3af' }}>{T('ساعات مقدرة', 'Est. Hours')}</div><div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: '#fbbf24' }}>{summary.estimated_hours || 0}</div></div>
          <div style={{ ...cardSt('#6b7280'), display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{T('حسب الأولوية', 'By Priority')}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Object.entries(summary.by_priority || {}).map(([k, v]) => v > 0 && (
                <span key={k} style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: `${pColors[k]}22`, color: pColors[k] }}>{v} {pLabels[k]}</span>
              ))}
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[{ key: 'list', icon: '📋', label: T('قائمة', 'List') }, { key: 'timeline', icon: '📅', label: T('جدول زمني', 'Timeline') }, { key: 'client', icon: '👤', label: T('حسب العميل', 'By Client') }].map(v => (
            <button key={v.key} onClick={() => setViewMode(v.key)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: viewMode === v.key ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)', color: viewMode === v.key ? '#60a5fa' : '#9ca3af' }}>{v.icon} {v.label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>{T('جاري التحميل...', 'Loading...')}</div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', background: 'rgba(26,32,56,0.6)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{T('لا توجد أعمال صيانة في هذه الفترة', 'No maintenance activities in this period')}</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>{T('جرب تغيير الفترة أو الفلاتر', 'Try changing the period or filters')}</div>
          </div>
        ) : (
          <>
            {/* LIST VIEW */}
            {viewMode === 'list' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.map((item, idx) => (
                  <div key={idx} style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden', borderInlineStart: `4px solid ${pColors[item.priority] || '#6b7280'}` }}>
                    <div onClick={() => toggleRow(idx)} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{isRTL ? (item.plan_name_ar || item.plan_name) : item.plan_name}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <span>👤 {isRTL ? (item.client_name_ar || item.client_name || '—') : (item.client_name || '—')}</span>
                          <span>📄 {item.contract_number}</span>
                          <span style={{ color: pColors[item.priority] }}>{recLabels[item.recurrence] || item.recurrence}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${pColors[item.priority]}22`, color: pColors[item.priority] }}>{pLabels[item.priority]}</span>
                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: item.is_overdue ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.15)', color: item.is_overdue ? '#f87171' : '#4ade80' }}>
                          {fmtDate(item.due_date)}{item.is_overdue ? ` (${T('متأخر', 'Overdue')} ${Math.abs(item.days_until)} ${T('يوم', 'd')})` : ''}
                        </span>
                        {item.auto_wo && <span style={{ fontSize: 11, color: '#4ade80' }}>⚡ {T('تلقائي', 'Auto WO')}</span>}
                        <span style={{ color: '#6b7280', fontSize: 18, transform: expandedRows[idx] ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }}>▾</span>
                      </div>
                    </div>
                    {expandedRows[idx] && (
                      <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, padding: '16px 0' }}>
                          <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{T('العقد', 'Contract')}</div><div style={{ fontSize: 14, color: '#d1d5db', marginTop: 2 }}>{isRTL ? (item.contract_title_ar || item.contract_title) : item.contract_title}</div></div>
                          {item.visits_remaining !== null && <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{T('زيارات متبقية', 'Visits Left')}</div><div style={{ fontSize: 14, color: item.visits_remaining < 3 ? '#f87171' : '#d1d5db', fontWeight: 600, marginTop: 2 }}>{item.visits_remaining}</div></div>}
                          {item.estimated_hours && <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{T('المدة المقدرة', 'Est. Duration')}</div><div style={{ fontSize: 14, color: '#d1d5db', marginTop: 2 }}>{item.estimated_hours} {T('ساعة', 'hrs')}</div></div>}
                          {item.client_phone && <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{T('هاتف العميل', 'Client Phone')}</div><div style={{ fontSize: 14, color: '#d1d5db', marginTop: 2, direction: 'ltr' }}>{item.client_phone}</div></div>}
                          {item.client_city && <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{T('المدينة', 'City')}</div><div style={{ fontSize: 14, color: '#d1d5db', marginTop: 2 }}>{item.client_city}</div></div>}
                        </div>
                        {item.equipment.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>⚙️ {T('المعدات', 'Equipment')} ({item.equipment.length})</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                              {item.equipment.map((eq, eIdx) => (
                                <div key={eIdx} style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                  <div style={{ fontWeight: 600, fontSize: 13, color: '#e5e7eb' }}>{isRTL ? (eq.name_ar || eq.name) : eq.name}</div>
                                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{[eq.category_name_ar || eq.category_name, eq.brand, eq.model, eq.serial_number].filter(Boolean).join(' · ')}</div>
                                  {(eq.location_name || eq.location_name_ar) && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📍 {isRTL ? (eq.location_name_ar || eq.location_name) : eq.location_name}{eq.floor ? ` - ${T('دور', 'Fl')} ${eq.floor}` : ''}</div>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {item.checklist && item.checklist.length > 0 && (
                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>📋 {T('قائمة الفحص', 'Checklist')} ({item.checklist.length})</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {item.checklist.map((ci, cIdx) => <div key={cIdx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#d1d5db' }}><span style={{ color: '#6b7280' }}>☐</span><span>{ci.text || ci.item || ci}</span></div>)}
                            </div>
                          </div>
                        )}
                        {(item.instructions || item.instructions_ar) && (
                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>📝 {T('التعليمات', 'Instructions')}</div>
                            <div style={{ fontSize: 13, color: '#d1d5db', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'rgba(255,255,255,0.02)', padding: 12, borderRadius: 8 }}>{isRTL ? (item.instructions_ar || item.instructions) : (item.instructions || item.instructions_ar)}</div>
                          </div>
                        )}
                        {item.description && <div style={{ marginTop: 10, fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>{item.description}</div>}
                        {/* Create WO Button */}
                        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end' }}>
                          <button onClick={(e) => { e.stopPropagation(); createWO(item); }}
                            style={{ padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer',
                              background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: '#fff',
                              fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
                              boxShadow: '0 4px 12px rgba(59,130,246,0.3)', transition: 'transform 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                            + {T('إنشاء أمر عمل', 'Create Work Order')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* TIMELINE VIEW */}
            {viewMode === 'timeline' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {Object.entries(groupedByDate).map(([date, items]) => {
                  const d = new Date(date);
                  const isToday = d.toDateString() === new Date().toDateString();
                  const isPast = d < new Date(new Date().toDateString());
                  return (
                    <div key={date}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <div style={{ width: 14, height: 14, borderRadius: '50%', background: isToday ? '#3b82f6' : isPast ? '#f87171' : '#4ade80', boxShadow: isToday ? '0 0 10px rgba(59,130,246,0.5)' : 'none' }} />
                        <div style={{ fontSize: 16, fontWeight: 700, color: isToday ? '#60a5fa' : isPast ? '#f87171' : '#e5e7eb' }}>
                          {fmtDate(date)}{isToday && <span style={{ fontSize: 12, fontWeight: 400, color: '#60a5fa', marginInlineStart: 8 }}>({T('اليوم', 'Today')})</span>}
                        </div>
                        <span style={{ fontSize: 12, color: '#6b7280', background: 'rgba(255,255,255,0.05)', padding: '2px 10px', borderRadius: 12 }}>{items.length} {T('عمل', 'task')}</span>
                        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingInlineStart: 26 }}>
                        {items.map((item, idx) => (
                          <div key={idx} style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '14px 18px', borderInlineStart: `3px solid ${pColors[item.priority]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{isRTL ? (item.plan_name_ar || item.plan_name) : item.plan_name}</div>
                              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>👤 {isRTL ? (item.client_name_ar || item.client_name || '—') : (item.client_name || '—')} · {item.contract_number}{item.equipment_count > 0 ? ` · ⚙️ ${item.equipment_count}` : ''}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${pColors[item.priority]}22`, color: pColors[item.priority] }}>{pLabels[item.priority]}</span>
                              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, background: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>{recLabels[item.recurrence]}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* CLIENT VIEW */}
            {viewMode === 'client' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {Object.entries(groupedByClient).map(([clientId, group]) => (
                  <div key={clientId} style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>👤 {isRTL ? (group.client_name_ar || group.client_name) : group.client_name}</div>
                      <span style={{ fontSize: 13, color: '#9ca3af', background: 'rgba(255,255,255,0.06)', padding: '4px 14px', borderRadius: 12 }}>{group.items.length} {T('عمل', 'tasks')}</span>
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>{[T('التاريخ', 'Date'), T('الخطة', 'Plan'), T('العقد', 'Contract'), T('التكرار', 'Recurrence'), T('الأولوية', 'Priority'), T('المعدات', 'Equip.')].map((h, i) => (
                            <th key={i} style={{ padding: '8px 10px', textAlign: isRTL ? 'right' : 'left', fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{h}</th>
                          ))}</tr>
                        </thead>
                        <tbody>
                          {group.items.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <td style={{ padding: 10, fontSize: 13, color: item.is_overdue ? '#f87171' : '#d1d5db', fontWeight: item.is_overdue ? 600 : 400 }}>{fmtDate(item.due_date)}</td>
                              <td style={{ padding: 10, fontSize: 13, color: '#e5e7eb', fontWeight: 500 }}>{isRTL ? (item.plan_name_ar || item.plan_name) : item.plan_name}</td>
                              <td style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>{item.contract_number}</td>
                              <td style={{ padding: 10 }}><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: 'rgba(96,165,250,0.12)', color: '#60a5fa' }}>{recLabels[item.recurrence]}</span></td>
                              <td style={{ padding: 10 }}><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: `${pColors[item.priority]}18`, color: pColors[item.priority] }}>{pLabels[item.priority]}</span></td>
                              <td style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>⚙️ {item.equipment_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default PreventiveSchedule;
